/**
 * RiderDetailModal Component
 * 라이더 주정산 상세 모달
 */
import React from "react";
import { RiderSettlement } from "../types/riderSettlement.types";
import { formatCurrency } from "../lib/riderSettlementHelpers";

interface RiderDetailModalProps {
    settlement: RiderSettlement;
    onClose: () => void;
    isRiderUser: boolean;
}

const RiderDetailModal: React.FC<RiderDetailModalProps> = ({
    settlement,
    onClose,
    isRiderUser
}) => {
    /** 배경 클릭 시 닫기 */
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="rider-detail-modal-overlay" onClick={handleBackdropClick}>
            <div className="rider-detail-modal" onClick={(e) => e.stopPropagation()}>
                {/* 헤더 */}
                <div className="rider-detail-modal-header">
                    <div>
                        <div className="subtitle">라이더 주정산 상세</div>
                        <h2>{settlement.riderName} ({settlement.riderId})</h2>
                    </div>
                    <button className="rider-detail-modal-close" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* 본문 */}
                <div className="rider-detail-modal-body">
                    {/* 기본 정보 */}
                    <div className="rider-detail-section">
                        <div className="rider-detail-section-title">
                            <i className="fas fa-user" style={{ marginRight: '8px' }}></i>
                            기본 정보
                        </div>
                        <div className="rider-detail-grid">
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">라이더 ID</span>
                                <span className="rider-detail-value">{settlement.riderId}</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">라이더명</span>
                                <span className="rider-detail-value">{settlement.riderName}</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">라이더 구분</span>
                                <span className="rider-detail-value">{settlement.riderType}</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">정산 시작일</span>
                                <span className="rider-detail-value">{settlement.settlementStartDate}</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">정산 종료일</span>
                                <span className="rider-detail-value">{settlement.settlementEndDate}</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">처리 건수</span>
                                <span className="rider-detail-value">{formatCurrency(settlement.processCount)}건</span>
                            </div>
                        </div>
                    </div>

                    {/* 배달료 정보 */}
                    <div className="rider-detail-section">
                        <div className="rider-detail-section-title">
                            <i className="fas fa-motorcycle" style={{ marginRight: '8px' }}></i>
                            배달료 정보
                        </div>
                        <div className="rider-detail-grid">
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">배달료</span>
                                <span className="rider-detail-value">{formatCurrency(settlement.deliveryFee)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">추가지급</span>
                                <span className="rider-detail-value">{formatCurrency(settlement.additionalPayment)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">과세배달료</span>
                                <span className="rider-detail-value">{formatCurrency(settlement.taxableDeliveryFee)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">월보수액</span>
                                <span className="rider-detail-value">{formatCurrency(settlement.monthlySalary)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">프로모션</span>
                                <span className="rider-detail-value">{formatCurrency(settlement.promotion)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">최종배달료</span>
                                <span className="rider-detail-value amount">{formatCurrency(settlement.finalDeliveryFee)}원</span>
                            </div>
                        </div>
                    </div>

                    {/* 수수료 정보 (라이더가 아닌 경우만 표시) */}
                    {!isRiderUser && (
                        <div className="rider-detail-section">
                            <div className="rider-detail-section-title">
                                <i className="fas fa-percentage" style={{ marginRight: '8px' }}></i>
                                수수료 정보
                            </div>
                            <div className="rider-detail-grid">
                                <div className="rider-detail-item">
                                    <span className="rider-detail-label">중개사 수수료</span>
                                    <span className="rider-detail-value negative">{formatCurrency(settlement.brokerManageFee)}원</span>
                                </div>
                                <div className="rider-detail-item">
                                    <span className="rider-detail-label">협력사 수수료</span>
                                    <span className="rider-detail-value negative">{formatCurrency(settlement.partnerOperationFee)}원</span>
                                </div>
                                <div className="rider-detail-item">
                                    <span className="rider-detail-label">총 운영수수료</span>
                                    <span className="rider-detail-value negative">{formatCurrency(settlement.totalOperationFee)}원</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 보험료 정보 */}
                    <div className="rider-detail-section">
                        <div className="rider-detail-section-title">
                            <i className="fas fa-shield-alt" style={{ marginRight: '8px' }}></i>
                            보험료 정보
                        </div>
                        <div className="rider-detail-grid">
                            {!isRiderUser && (
                                <div className="rider-detail-item">
                                    <span className="rider-detail-label">라이더 고용보험</span>
                                    <span className="rider-detail-value negative">{formatCurrency(settlement.riderEmployment)}원</span>
                                </div>
                            )}
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">라이더 산재보험</span>
                                <span className="rider-detail-value negative">{formatCurrency(settlement.riderIndustrial)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">시간제 보험료</span>
                                <span className="rider-detail-value negative">{formatCurrency(settlement.hourlyInsurance)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">보험료 총합</span>
                                <span className="rider-detail-value negative">{formatCurrency(settlement.insuranceTotal)}원</span>
                            </div>
                        </div>
                    </div>

                    {/* 세금 정보 */}
                    <div className="rider-detail-section">
                        <div className="rider-detail-section-title">
                            <i className="fas fa-file-invoice-dollar" style={{ marginRight: '8px' }}></i>
                            세금 정보
                        </div>
                        <div className="rider-detail-grid">
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">소득세</span>
                                <span className="rider-detail-value negative">{formatCurrency(settlement.incomeTax)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">지방세</span>
                                <span className="rider-detail-value negative">{formatCurrency(settlement.localTax)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">원천징수</span>
                                <span className="rider-detail-value negative">{formatCurrency(settlement.withholdingTax)}원</span>
                            </div>
                        </div>
                    </div>

                    {/* 정산 정보 */}
                    <div className="rider-detail-section">
                        <div className="rider-detail-section-title">
                            <i className="fas fa-calculator" style={{ marginRight: '8px' }}></i>
                            정산 정보
                        </div>
                        <div className="rider-detail-grid">
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">선정산 배달료</span>
                                <span className="rider-detail-value">{formatCurrency(settlement.advanceSettlement)}원</span>
                            </div>
                            <div className="rider-detail-item">
                                <span className="rider-detail-label">일차감</span>
                                <span className="rider-detail-value negative">{formatCurrency(settlement.dailyDeduction)}원</span>
                            </div>
                            <div className="rider-detail-item highlight">
                                <span className="rider-detail-label">실지급 배달료</span>
                                <span className="rider-detail-value amount">{formatCurrency(settlement.actualPayment)}원</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 푸터 */}
                <div className="rider-detail-modal-footer">
                    <button onClick={onClose}>
                        <i className="fas fa-times" style={{ marginRight: '8px' }}></i>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RiderDetailModal;
